import { Box } from '@mui/material'
import { IconSend } from '@tabler/icons-react'
import { toast } from 'react-hot-toast'
import type { Draft } from 'types/graphql'

import { useMutation } from '@redwoodjs/web'

import DayTooltip from 'src/components/DayTooltip/DayTooltip'
import SidebarButton from 'src/components/Sidebar/SidebarButton/SidebarButton'

import { GET_DRAFT } from '../queries'
import { useValidatedFromAddress } from '../utils'

const SEND_DRAFT = gql`
  mutation SendDraft($id: String!, $workspaceId: String!) {
    sendDraft(id: $id, workspaceId: $workspaceId) {
      id
    }
  }
`

const buttonSx = {
  width: '110px',
  height: '40px',
  '& .MuiChip-label': {
    fontSize: '.875rem',
  },
  background: (theme) => theme.palette.secondary.main,
  color: (theme) => theme.palette.secondary.contrastText,
  '& .MuiChip-icon': {
    color: (theme) => theme.palette.secondary.contrastText,
  },
  '&&:hover': {
    background: (theme) => theme.palette.secondary.main,
  },
}

const SendDraftButton = ({
  draft,
  handleDraftSent,
  status,
  pageAiUnderway,
}: {
  draft: Draft
  handleDraftSent: () => void
  status: string
  pageAiUnderway: boolean
}) => {
  const [sendDraft, { loading: sending }] = useMutation(SEND_DRAFT, {
    onCompleted: () => {
      toast.success('Draft sent')
      handleDraftSent()
    },
    onError: ({ graphQLErrors }) => {
      let errorMessage = 'Failed to send draft'
      for (const error of graphQLErrors) {
        if (
          error.extensions.originalError?.['message'].includes('invalid_grant')
        ) {
          errorMessage =
            'Invalid credentials. Please try re-authenticating your work account.'
        }
      }
      toast.error(errorMessage)
    },
    refetchQueries: [GET_DRAFT],
    awaitRefetchQueries: true,
  })

  const { validatedFromAddress } = useValidatedFromAddress(draft)

  const requiredFields = [
    draft.email?.to,
    draft.email?.subject,
    draft.email?.from,
  ]

  const shouldDisable =
    sending ||
    status === 'saving' ||
    status === 'dirty' ||
    status === 'error' ||
    draft.status === 'SENT' ||
    requiredFields.some((field) => !field) ||
    pageAiUnderway ||
    !validatedFromAddress

  let label
  if (sending) {
    label = 'Sending...'
  } else if (status === 'saving' || status === 'dirty') {
    label = 'Saving...'
  } else if (draft.status === 'SENT') {
    label = 'Sent'
  } else if (pageAiUnderway) {
    label = 'Writing...'
  } else {
    label = 'Send'
  }

  return (
    <Box sx={{ width: 'fit-content' }}>
      <DayTooltip
        title={
          !validatedFromAddress
            ? 'Please connect a work account to send emails'
            : ''
        }
        placement="auto"
      >
        <SidebarButton
          label={label}
          sx={buttonSx}
          icon={<IconSend size={20} />}
          disabled={shouldDisable}
          onClick={() =>
            sendDraft({
              variables: { id: draft.id, workspaceId: draft.workspaceId },
            })
          }
        />
      </DayTooltip>
    </Box>
  )
}

export default SendDraftButton
